import { ref, computed } from 'vue'
import type { Ref } from 'vue'

/**
 * Service Management Composable
 * 
 * Handles all service-related operations including:
 * - Service execution and lifecycle
 * - Service node interactions
 * - Service running state management
 * - Service execution data handling
 */
export function useServiceManagement(
  nodes: Ref<any[]>,
  runningNodes: Ref<Set<string>>,
  nodeExecutionData: Ref<Map<string, any>>,
  openServiceDetails: (nodeId: string, nodeData: any) => void,
  openAddServicePanel: (parentNodeId: string | null) => void,
  openRunServiceModal: (nodeId: string, nodeData: any) => void,
  closeRunServiceModal: () => void,
  restoreServiceDetailsOnModalClose: Ref<boolean>,
  runModalWasCancelled: Ref<boolean>,
  runServiceNodeId: Ref<string | null>,
  runServiceData: Ref<any>
) {
  
  // Service execution state
  const executingServices = ref<Set<string>>(new Set())
  
  // Computed properties
  const isAnyServiceRunning = computed(() => runningNodes.value.size > 0)
  const executingServiceCount = computed(() => executingServices.value.size)
  
  /**
   * Handle adding a service with a specific parent
   */
  function handleAddService(parentNodeId: string) {
    openAddServicePanel(parentNodeId)
  }
  
  /**
   * Handle adding a service from the main menu (no parent)
   */
  function handleAddServiceFromMenu() {
    // For menu-triggered add service, create standalone node with no parent
    openAddServicePanel(null)
  }
  
  /**
   * Handle running a service in test mode
   */
  function handleRunService(nodeId: string) {
    const node = nodes.value.find((n) => n.id === nodeId)
    if (node) {
      openRunServiceModal(nodeId, node.data)
    }
  }
  
  /**
   * Run one service on its own. There is no engine path for a single service — a run executes a
   * published version — so this closes the modal and does nothing else. Live per-service state on the
   * canvas comes from watching a real run (see useRunProgress); it is not produced here.
   */
  function handleExecuteService(_nodeId: string, _inputs: Record<string, any>) {
    closeRunServiceModal()
  }

  /**
   * Handle service run from node interaction (editor vs history mode)
   */
  function handleServiceRun(nodeId: string, nodeData: any) {
    // This function handles the logic for when a service is selected to run
    // In editor mode, it shows configuration details
    // In history mode, it shows execution details
    openServiceDetails(nodeId, nodeData)
  }
  
  /**
   * Stop a running service
   */
  function stopService(nodeId: string) {
    const nodeIndex = nodes.value.findIndex((n) => n.id === nodeId)
    if (nodeIndex !== -1) {
      // Update execution data
      const existingData = nodeExecutionData.value.get(nodeId)
      if (existingData) {
        const stoppedData = {
          ...existingData,
          endTime: new Date().toISOString(),
          status: "stopped",
          output: "Service execution was stopped by user"
        }
        nodeExecutionData.value.set(nodeId, stoppedData)
        ;(nodes.value[nodeIndex].data as any).executionData = stoppedData
      }
      
      // Remove from running sets
      runningNodes.value.delete(nodeId)
      executingServices.value.delete(nodeId)
    }
  }
  
  /**
   * Stop all running services
   */
  function stopAllServices() {
    for (const nodeId of runningNodes.value) {
      stopService(nodeId)
    }
  }
  
  /**
   * Get execution status for a service
   */
  function getServiceExecutionStatus(nodeId: string) {
    return nodeExecutionData.value.get(nodeId)
  }
  
  /**
   * Check if a service is currently running
   */
  function isServiceRunning(nodeId: string): boolean {
    return runningNodes.value.has(nodeId)
  }
  
  /**
   * Get all running service IDs
   */
  function getRunningServices(): string[] {
    return Array.from(runningNodes.value)
  }
  
  /**
   * Clear execution data for a service
   */
  function clearServiceExecutionData(nodeId: string) {
    nodeExecutionData.value.delete(nodeId)
    const nodeIndex = nodes.value.findIndex((n) => n.id === nodeId)
    if (nodeIndex !== -1) {
      delete (nodes.value[nodeIndex].data as any).executionData
    }
    runningNodes.value.delete(nodeId)
    executingServices.value.delete(nodeId)
  }
  
  /**
   * Clear all execution data
   */
  function clearAllExecutionData() {
    nodeExecutionData.value.clear()
    executingServices.value.clear()
    runningNodes.value.clear()
    
    // Reset all nodes
    nodes.value.forEach((node) => {
      delete (node.data as any).executionData
    })
  }
  
  /**
   * Handle run modal cancellation
   */
  function handleRunModalCancel() {
    runModalWasCancelled.value = true
  }
  
  /**
   * Handle run modal hide with state restoration
   */
  function handleHideRunModal() {
    // If modal was cancelled and we should restore service details panel, do so
    if (
      restoreServiceDetailsOnModalClose.value &&
      runModalWasCancelled.value &&
      runServiceNodeId.value &&
      runServiceData.value
    ) {
      openServiceDetails(runServiceNodeId.value, runServiceData.value)
    }
    
    // Reset state using composable
    closeRunServiceModal()
  }
  
  return {
    // State
    executingServices: executingServices as Ref<Set<string>>,
    
    // Computed
    isAnyServiceRunning,
    executingServiceCount,
    
    // Service interaction methods
    handleAddService,
    handleAddServiceFromMenu,
    handleRunService,
    handleServiceRun,
    
    // Service execution methods
    handleExecuteService,
    stopService,
    stopAllServices,
    
    // Modal handling methods
    handleRunModalCancel,
    handleHideRunModal,
    
    // Service state queries
    getServiceExecutionStatus,
    isServiceRunning,
    getRunningServices,
    
    // Service data management
    clearServiceExecutionData,
    clearAllExecutionData,
  }
}